/* eslint-disable camelcase */
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import React from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { CREATE_CONTACT } from '../../../queries/contacts';
import { Mutation } from '../../../types/schema';
import { submitContactAndHubSpot } from '../../../utils/hubspot';
import logging from '../../../utils/logging';
import ContactFormTemplate from './template';
import {
  isLookingForJob as checkIsLookingForJob,
  LOOKING_FOR_JOB_KEY,
} from '../../../utils/object';
import { useLocalStorage } from 'react-use';
import { hideChat } from '../../../utils/chat';

export type FormFields = {
  name: string;
  email: string;
  message: string;
  phone: string;
  companyName?: string;
  companySegment?: string;
  role?: string;
};

export type ContactFormProps = {
  background: 'white' | 'primary';
  hideMessage?: boolean;
  hidePhone?: boolean;
  sendToEmail?: boolean;
  buttonText?: string;
  hubspotFormId?: string;
  title?: string;
  description?: string;
  destinationUrl?: string;
  className?: string;
  gtagConversionEvent?: string;
  isCompany?: boolean;
  showTermsCheckBox?: boolean;
  urlOnNewTab?: boolean;
  onSuccess?: (data: FormFields) => void;
  defaultValues?: { name?: string; email?: string; phone?: string };
};

const ContactForm: React.FC<ContactFormProps> = ({
  hubspotFormId,
  sendToEmail,
  gtagConversionEvent,
  onSuccess,
  defaultValues,
  ...props
}) => {
  const router = useRouter();

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<boolean | string>(false);
  const [success, setSuccess] = React.useState(false);
  const [isLookingForJob, setIsLookingForJob] = useLocalStorage(LOOKING_FOR_JOB_KEY, false);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const [createContact] = useMutation<Mutation>(CREATE_CONTACT);

  const onSubmit = async (data: FormFields, newsChecked?: boolean) => {
    setError(false);
    setLoading(true);

    logging.info('1 - ContactForm submit started', {
      sendToEmail: !!sendToEmail,
      hasHubspotFormId: !!hubspotFormId,
      hideMessage: !!props.hideMessage,
      hidePhone: !!props.hidePhone,
      destinationUrl: props.destinationUrl,
      pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
    });

    if (checkIsLookingForJob(data.message)) {
      setIsLookingForJob(true);
      hideChat();
      logging.info('User is looking for job', data);
      return;
    }

    // Open the destination tab (e.g. WhatsApp) synchronously, before any await,
    // so it stays tied to the user's click gesture and is not blocked as a popup.
    // The lead submission below still runs; if HubSpot fails the tab is already open.
    let destinationWindow: Window | null = null;
    if (props.destinationUrl && props.urlOnNewTab) {
      destinationWindow = window.open(props.destinationUrl, '_blank');
    }

    try {
      const title = document.title?.split(' - ')?.[0] || document.title || props.title || '';
      const utmSource = router.query?.utm_source as string;
      const utmMedium = router.query?.utm_medium as string;
      const utmCampaign = router.query?.utm_campaign as string;

      await submitContactAndHubSpot({
        hubspotFormId: hubspotFormId || '',
        hubspotPortalId: process.env.NEXT_PUBLIC_HUBSPOT_PORTAL_ID || '',
        hubspotFields: {
          firstname: data.name,
          email: data.email,
          hs_language: navigator.language.toLowerCase() || router.locale || 'pt-br',
          ...(!props.hideMessage && { message: data.message }),
          ...(!props.hidePhone && { phone: data.phone }),
          ...(utmSource && { utm_source: utmSource }),
          ...(utmMedium && { utm_medium: utmMedium }),
          ...(utmCampaign && { utm_campaign: utmCampaign }),
          ...(props.isCompany && {
            jobtitle: data.role,
            ['0-2/name']: data.companyName,
            ['0-2/segment']: data.companySegment,
          }),
        },
        hubspotContext: {
          pageName: title,
          pageUri: window.location.href,
        },
        newsletter: newsChecked,
        contactVariables: {
          name: data.name,
          email: data.email,
          ...(!props.hidePhone && { phone: data.phone }),
          ...(!props.hideMessage && { message: data.message }),
          formTitle: props.title,
          url: window.location.href,
        },
        createContact: sendToEmail
          ? createContact
          : async () => {
              logging.info('2 - ContactForm skipped createContactAndSendEmailWebsite', {
                reason: 'sendToEmail is false',
                pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
              });
              /* skip contact creation */
            },
        executeRecaptcha: executeRecaptcha || undefined,
        gtagConversionEvent,
        setError: (err) => setError(err),
        setLoading,
      });

      const { default: ReactPixel } = await import('react-facebook-pixel');
      ReactPixel.track('Lead');
      ReactPixel.track('Contact');

      setLoading(false);
      setSuccess(true);
      onSuccess?.(data);
      if (props.destinationUrl) {
        if (props.urlOnNewTab) {
          // If the early window.open was blocked (returned null), fall back to
          // navigating the current tab so the user still reaches the destination.
          if (!destinationWindow) {
            window.location.href = props.destinationUrl;
          }
        } else {
          router.push(props.destinationUrl);
        }
      }
    } catch (error) {
      logging.error('Error on ContactForm submit', {
        error,
        errorMessage: (error as { message: string }).message,
        data,
      });
      setError(true);
      setLoading(false);
      setSuccess(false);
    }
  };

  return (
    <ContactFormTemplate
      {...props}
      loading={loading}
      onSubmit={onSubmit}
      error={error}
      locale={router.locale as string}
      sitekey={process.env.NEXT_PUBLIC_API_KEY_CAPTCHA as string}
      success={success}
      setError={(value) => setError(value)}
      isLookingForJob={!!isLookingForJob}
      defaultValues={defaultValues}
    />
  );
};

export default ContactForm;
